import { CloseIcon } from "@bvhi/ui-icons";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  OutlinedSelectProps,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { ReactNode } from "react";
import { Control, Controller } from "react-hook-form";
import { IconButton } from "./IconButton";

type Props = object & SelectProps;

export const Select = ({ children, ...props }: Props) => {
  return <MuiSelect {...props}>{children}</MuiSelect>;
};

type BVHISelectProps = {
  handleChange: (value: string) => void;
  handleReset?: () => void;
  showReset?: boolean;
  items: { key: string; value: string; label: string | ReactNode }[];
} & SelectProps &
  OutlinedSelectProps;

export const BVHISelect = ({
  fullWidth,
  showReset = false,
  handleChange,
  handleReset,
  value,
  size,
  label,
  name,
  items,
  ...props
}: BVHISelectProps) => {
  return (
    <FormControl fullWidth={fullWidth} size={size}>
      <InputLabel id={`select-${name}`} shrink>
        {label}
      </InputLabel>
      <Select
        displayEmpty
        label={label}
        labelId={`select-${name}`}
        notched
        size={size}
        value={value}
        endAdornment={
          (showReset && value && !Array.isArray(value)) || (Array.isArray(value) && value.length > 0) ? (
            <IconButton
              onClick={handleReset}
              sx={{ mr: 2 }}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
            >
              <CloseIcon sx={{ width: 12, height: 12 }} />
            </IconButton>
          ) : null
        }
        onChange={(e) => {
          handleChange(e.target.value as string);
        }}
        {...props}
      >
        {items.map((item) => {
          return (
            <MenuItem key={item.key} value={item.value}>
              {item.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export type FormSelectProps = {
  control: Control;
  selectLabel: string;
  name: string;
  items: { value: string; label: string }[];
  changeCallback?: (e: SelectChangeEvent<unknown>) => void;
} & Omit<Props, "variant">;

export function FormSelect({
  name,
  disabled,
  control,
  selectLabel,
  changeCallback,
  items,
  size = "small",
  placeholder = "Bitte geben Sie einen Wert an",
  ...props
}: FormSelectProps) {
  return (
    <FormControl fullWidth size={size}>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
          <>
            <InputLabel error={!disabled && !!error} id={`select_label-${name}`} shrink>
              {selectLabel}
            </InputLabel>
            <Select
              disabled={disabled}
              displayEmpty
              error={!disabled && !!error}
              label={selectLabel}
              labelId={`select_label-${name}`}
              name={name}
              onBlur={onBlur}
              renderValue={value !== undefined ? undefined : () => <div style={{ color: "#92969c" }}>{placeholder}</div>}
              size={size}
              value={(value as string) || ""}
              onChange={(e) => {
                onChange(e);
                if (changeCallback) changeCallback(e);
              }}
              {...props}
            >
              {items.map((item) => (
                <MenuItem key={`${name}-${item.value}`} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error>{!disabled && error ? error.message : " "}</FormHelperText>
          </>
        )}
      />
    </FormControl>
  );
}
